import { addHours, addMinutes } from 'date-fns';
import { getFormatLocale, type FormatOverrides } from './numberFormat';
import { UserPreferences } from './user-preferences/UserPreferences';

export type { FormatOverrides } from './numberFormat';
export { COPY_LOCALE } from './numberFormat';

export function isValidEmail(email: string): boolean {
  const emailRegex = /^[A-Za-z0-9._%-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,4}$/;
  return emailRegex.test(email);
}

/** Parse a date string safely. YYYY-MM-DD is parsed as local midnight (not UTC). */
export const parseLocalDate = (input: string | Date): Date => {
  if (input instanceof Date) return input;
  // YYYY-MM-DD → local midnight to avoid UTC shift
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(input);
  if (match) return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return new Date(input);
};

// ── Locale & timezone aware date formatters ──────────────────────────

/** Returns the user's preferred IANA timezone (e.g. "Europe/Berlin"), or undefined for browser default.
 *  Priority: override → UserPreferences (backend-synced) → browser timezone → undefined */
export function getFormatTimeZone(override?: string): string | undefined {
  if (override) return override;
  const stored = UserPreferences.getAll().regional.timezone;
  if (stored) return stored;
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
}

/** Build Intl options with timeZone injected. */
const withTz = <T extends Intl.DateTimeFormatOptions>(opts: T, tzOverride?: string): T => {
  const tz = getFormatTimeZone(tzOverride);
  return tz ? { ...opts, timeZone: tz } : opts;
};

/** A bare "YYYY-MM-DD" — a calendar day, not an instant. */
const isDateOnly = (input: string | Date): boolean =>
  typeof input === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(input);

/**
 * The zone a date is printed in. A calendar day is parsed as the browser's
 * local midnight (`parseLocalDate`) and must be printed in that same clock —
 * in the user's preferred zone a viewer west of the browser's would read
 * the day before (a show's 15 Nov printed as 14 Nov).
 */
const withDateTz = <T extends Intl.DateTimeFormatOptions>(
  input: string | Date,
  opts: T,
  tzOverride?: string,
): T => (isDateOnly(input) ? opts : withTz(opts, tzOverride));

/** Semantic date format presets — "how detailed should the date be?" */
type DatePreset = 'compact' | 'standard' | 'full';
type DateFormatOptions = {
  month?: 'numeric' | '2-digit' | 'short' | 'long';
  year?: 'numeric' | '2-digit';
};

const DATE_PRESETS: Record<DatePreset, DateFormatOptions> = {
  compact: { month: 'numeric' }, // "29.09.2025" / "9/29/2025"
  standard: { month: 'short' }, // "29 сент. 2025 г." / "Sep 29, 2025"
  full: { month: 'long' }, // "29 сентября 2025 г." / "September 29, 2025"
};

/** Format a date for display. Accepts a semantic preset or custom Intl options.
 *  - `formatDate(date)` → compact numeric "29.09.2025"
 *  - `formatDate(date, 'standard')` → short month "Sep 29, 2025"
 *  - `formatDate(date, 'full')` → long month "September 29, 2025"
 *  - `formatDate(date, { month: '2-digit', year: '2-digit' })` → custom */
export const formatDate = (
  inputDate: string | Date,
  options?: DatePreset | DateFormatOptions,
  overrides?: FormatOverrides,
) => {
  if (!inputDate) return '';
  const resolved: DateFormatOptions =
    typeof options === 'string' ? DATE_PRESETS[options] : (options ?? DATE_PRESETS.compact);
  const date = parseLocalDate(inputDate);
  return date.toLocaleDateString(
    getFormatLocale(overrides?.locale),
    withDateTz(
      inputDate,
      {
        day: 'numeric',
        month: resolved.month ?? 'numeric',
        year: resolved.year ?? 'numeric',
      },
      overrides?.timezone,
    ),
  );
};

/** "Mar 5" / "5" / "March 5" — month and day, controlled by month format */
const formatDateMonthDay = (
  date: Date,
  monthFormat: 'numeric' | '2-digit' | 'short' | 'long' = 'short',
  overrides?: FormatOverrides,
): string => {
  return date.toLocaleDateString(
    getFormatLocale(overrides?.locale),
    withTz({ month: monthFormat, day: 'numeric' }, overrides?.timezone),
  );
};

/** "March" — the month alone, for copy that carries no year of its own */
export const formatMonth = (date: Date, overrides?: FormatOverrides): string => {
  return date.toLocaleDateString(
    getFormatLocale(overrides?.locale),
    withTz({ month: 'long' }, overrides?.timezone),
  );
};

/** "March 2026" — full month and year */
export const formatMonthYear = (date: Date, overrides?: FormatOverrides): string => {
  return date.toLocaleDateString(
    getFormatLocale(overrides?.locale),
    withTz({ month: 'long', year: 'numeric' }, overrides?.timezone),
  );
};

/** Returns hour12 option based on user's time_format preference (0=auto, 1=12h, 2=24h). */
const getHour12 = (timeFormatIdOverride?: number): boolean | undefined => {
  const tf = timeFormatIdOverride ?? UserPreferences.getAll().regional.time_format?.id;
  if (tf === 1) return true;
  if (tf === 2) return false;
  return undefined; // auto — let locale decide
};

/** Format time for display. Accepts a semantic preset.
 *  - `formatTime(date)` → "14:30" — compact (default)
 *  - `formatTime(date, 'standard')` → "14:30" — same as compact
 *  - `formatTime(date, 'full')` → "14:30:00" — with seconds */
export const formatTime = (
  date: Date,
  preset?: DatePreset,
  overrides?: FormatOverrides,
): string => {
  const opts: Intl.DateTimeFormatOptions = {
    hour: '2-digit',
    minute: '2-digit',
    hour12: getHour12(overrides?.timeFormatId),
  };
  if (preset === 'full') {
    opts.second = '2-digit';
  }
  return date.toLocaleTimeString(
    getFormatLocale(overrides?.locale),
    withTz(opts, overrides?.timezone),
  );
};

/** Format date + time. Accepts the same presets as `formatDate`.
 *  - `formatDateTime(date)` → "29.09.2025, 14:30"
 *  - `formatDateTime(date, 'standard')` → "Sep 29, 2025, 14:30"
 *  - `formatDateTime(date, 'full')` → "September 29, 2025, 14:30" */
export const formatDateTime = (
  date: Date,
  preset?: DatePreset,
  overrides?: FormatOverrides,
): string => {
  const resolved = DATE_PRESETS[preset ?? 'compact'];
  return date.toLocaleString(
    getFormatLocale(overrides?.locale),
    withTz(
      {
        day: 'numeric',
        month: resolved.month ?? 'numeric',
        year: resolved.year ?? 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: getHour12(overrides?.timeFormatId),
      },
      overrides?.timezone,
    ),
  );
};

export const formatDateByGap = (
  inputDate: string,
  gap?: string | number,
  overrides?: FormatOverrides,
) => {
  if (!inputDate) return '';
  const date = parseLocalDate(inputDate);
  if (gap && gap.toString() === '2')
    return date.toLocaleDateString(
      getFormatLocale(overrides?.locale),
      withDateTz(inputDate, { month: 'short', year: 'numeric' }, overrides?.timezone),
    );
  if (gap && gap.toString() === '3') return String(date.getFullYear());
  return formatDate(inputDate, 'standard', overrides);
};

/** Compact chart x-axis label — respects the selected gap and drops the year to save space.
 *  daily (gap 1) → "27.05" (day.month), monthly (gap 2) → "May", yearly (gap 3) → "2025".
 *  Unlike a fixed-length truncation, this is locale-safe (no "07.10.2025" → "07.1" artifacts). */
export const formatAxisDate = (
  inputDate: string,
  gap?: string | number,
  overrides?: FormatOverrides,
): string => {
  if (!inputDate) return '';
  const date = parseLocalDate(inputDate);
  if (gap && gap.toString() === '3') return String(date.getFullYear());
  if (gap && gap.toString() === '2')
    return date.toLocaleDateString(
      getFormatLocale(overrides?.locale),
      withDateTz(inputDate, { month: 'short' }, overrides?.timezone),
    );
  return date.toLocaleDateString(
    getFormatLocale(overrides?.locale),
    withDateTz(inputDate, { day: 'numeric', month: 'numeric' }, overrides?.timezone),
  );
};

// ── API date helpers (for sending to backend) ────────────────────────

/** "2026-03-05" — date-only for API (no timezone conversion, dates are calendar dates) */
export const toApiDate = (date: Date | string): string => {
  if (typeof date === 'string') {
    // If already YYYY-MM-DD, return as-is to avoid UTC-parsing timezone shift
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) return date;
    return toApiDate(new Date(date));
  }
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

/** "2026-03-05T14:30:00Z" — UTC ISO 8601 datetime for API */
export const toApiDateTime = (date: Date | string): string => {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toISOString();
};

// ── Legacy ───────────────────────────────────────────────────────────

/** @deprecated Use formatDate() instead — this manually parses UTC offsets */
export const formatDateUtc = (dateString: string) => {
  if (!dateString) return '';
  const isIncreased = dateString.includes('+');
  const [dateWithoutUtc, UTC] = dateString.split(isIncreased ? '+' : '-');
  const [hours, minutes] = UTC.split(':').map(Number);
  const userTimeZoneOffsetInMinutes = new Date().getTimezoneOffset() * -1;
  const increasedMinutes = isIncreased ? minutes : minutes * -1;
  let resultDate = new Date(dateWithoutUtc);
  resultDate = addHours(resultDate, isIncreased ? hours : hours * -1);
  resultDate = addMinutes(resultDate, increasedMinutes + userTimeZoneOffsetInMinutes);

  // Already converted to local time — format without applying timezone again
  return resultDate.toLocaleDateString(getFormatLocale(), {
    day: 'numeric',
    month: 'numeric',
    year: 'numeric',
  });
};

/** Format a date range for display. Accepts a semantic preset.
 *  - `formatDateRange(obj)` → "29.09 – 05.10" — compact (default)
 *  - `formatDateRange(obj, 'standard')` → "Sep 29 – Oct 5" — short month
 *  - `formatDateRange(obj, 'full')` → "September 29 – October 5" — long month */
export function formatDateRange(
  dateObject: { started_at: string; finished_at: string },
  preset?: DatePreset,
  overrides?: FormatOverrides,
) {
  const locale = getFormatLocale(overrides?.locale);
  const resolved = DATE_PRESETS[preset ?? 'compact'];
  const options: Intl.DateTimeFormatOptions = withTz(
    {
      month: resolved.month ?? 'numeric',
      day: 'numeric',
    },
    overrides?.timezone,
  );
  const startedAt = parseLocalDate(dateObject.started_at).toLocaleDateString(locale, options);
  const finishedAt = parseLocalDate(dateObject.finished_at).toLocaleDateString(locale, options);
  return `${startedAt || ''} – ${finishedAt || ''}`;
}

/** Compact date range with smart collapsing. Accepts a semantic preset.
 *  - `formatDateRangeCompact(obj)` → "29.03–05.04, 2025" — compact (default)
 *  - `formatDateRangeCompact(obj, 'standard')` → "Mar 5–29, 2025" — short month
 *  - `formatDateRangeCompact(obj, 'full')` → "March 5–29, 2025" — long month */
export function formatDateRangeCompact(
  dateObject: { started_at: string; finished_at: string },
  preset?: DatePreset,
  overrides?: FormatOverrides,
) {
  const start = parseLocalDate(dateObject.started_at);
  const end = parseLocalDate(dateObject.finished_at);
  const resolved = preset ?? 'compact';
  const monthFormat = DATE_PRESETS[resolved].month ?? 'numeric';

  const sameYear = start.getFullYear() === end.getFullYear();
  const sameMonth = sameYear && start.getMonth() === end.getMonth();

  if (sameMonth) {
    return `${formatDateMonthDay(start, monthFormat, overrides)}–${formatDateMonthDay(end, monthFormat, overrides)}, ${start.getFullYear()}`;
  }

  if (sameYear) {
    return `${formatDateMonthDay(start, monthFormat, overrides)} – ${formatDateMonthDay(end, monthFormat, overrides)}, ${start.getFullYear()}`;
  }

  return `${formatDate(start, resolved, overrides)} – ${formatDate(end, resolved, overrides)}`;
}

export interface ErrorDataResponse {
  error: {
    status: number;
    data: {
      errors: { [key: string]: string[] };
      message?: string;
      data?: { field: string; messages: string[] }[];
    };
  };
}

export const debounce = <T extends unknown[]>(fn: (...args: T) => void, delay: number) => {
  let timeoutId: ReturnType<typeof setTimeout>;

  return (...args: T) => {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      fn(...args);
    }, delay);
  };
};

export const getQueryStringFromParams = (
  params: Array<string | number> | undefined = [],
  key: string,
  rootKey = 'filters',
) => {
  if (Array.isArray(params)) {
    const normalizedKey = key ? key.replace('.', '][') : key;
    return params
      .map((id) => {
        const encodeId = encodeURIComponent(id ?? '');
        return `${rootKey}[${normalizedKey}][]=${encodeId}`;
      })
      .join('&');
  }
  return '';
};

export const getSearchValue = (search = '') => {
  return search?.length > 0 && search?.length < 3 ? '' : search;
};

export const getTrimmedUrl = (url?: string) => {
  if (!url) {
    return '';
  }
  let newUrl = url?.replace('http://', '')?.replace('https://', '');
  if (newUrl.charAt(0) === '/') {
    newUrl = newUrl.slice(1);
  }
  if (newUrl.charAt(newUrl.length - 1) === '/') {
    newUrl = newUrl.slice(0, -1);
  }
  return newUrl;
};

export const generateUniqueNumericId = (): number => {
  const timestamp = Date.now();
  const randomPart = Math.floor(Math.random() * 10000);
  return timestamp * 10000 + randomPart;
};
export const generateUniqueStringId = (): string => {
  const timestamp = Date.now();
  const randomPart = Math.floor(Math.random() * 10000);
  const result = timestamp * 10000 + randomPart;
  return result.toString();
};

export function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()?.split(';').shift() || null;
  return null;
}

const getRootDomain = (domain: string): string | null => {
  const trimmed = domain.trim();
  if (!trimmed) {
    return null;
  }

  const withoutLeadingDot = trimmed.replace(/^\.+/, '');
  if (!withoutLeadingDot) {
    return null;
  }

  const segments = withoutLeadingDot.split('.');
  if (segments.length <= 1) {
    return withoutLeadingDot;
  }

  return segments.slice(-2).join('.');
};

export const deleteCookie = (
  name: string,
  options?: {
    path?: string;
    domain?: string;
    // when true, also deletes the cookie on the root domain (e.g. .example.com)
    rootDomain?: boolean;
  },
) => {
  if (typeof document === 'undefined') {
    return;
  }

  const expires = 'Thu, 01 Jan 1970 00:00:00 UTC';
  const path = options?.path ?? '/';

  const domains = new Set<string>();
  domains.add('');

  if (options?.domain) {
    const userDomain = options.domain.trim();
    if (userDomain) {
      const normalized = getRootDomain(userDomain);
      if (normalized) {
        domains.add(`.${normalized}`);
      }

      const provided = userDomain.startsWith('.')
        ? userDomain
        : `.${userDomain.replace(/^\.+/, '')}`;
      domains.add(provided);
    }
  }

  if (options?.rootDomain && typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname) {
      const normalizedHost = getRootDomain(hostname);
      if (normalizedHost) {
        domains.add(`.${normalizedHost}`);
      }
    }
  }

  domains.forEach((domain) => {
    const domainPart = domain ? ` domain=${domain};` : '';
    document.cookie = `${name}=; expires=${expires}; path=${path};${domainPart}`;
  });
};

export const getRangePickerPlaceholder = (): [string, string] => {
  const currentDate = new Date();

  const oneMonthLater = new Date(currentDate);
  oneMonthLater.setMonth(currentDate.getMonth() + 1);
  const oneMonthAndThreeDaysLater = new Date(oneMonthLater);
  oneMonthAndThreeDaysLater.setDate(oneMonthLater.getDate() + 3);

  return [formatDate(oneMonthLater.toString()), formatDate(oneMonthAndThreeDaysLater.toString())];
};

export const multiplyAccurate = (a: number, b: number, toFixed = 2) => {
  const factor =
    10 **
    Math.max(a.toString().split('.')[1]?.length || 0, b.toString().split('.')[1]?.length || 0);

  const res = (a * factor * (b * factor)) / (factor * factor);
  if (toFixed) {
    return res.toFixed(toFixed);
  }

  return res;
};

export function patchHtml(html: string) {
  const doc = new DOMParser().parseFromString(html, 'text/html');

  doc.querySelectorAll('video').forEach((videoEl) => {
    videoEl.setAttribute('autoplay', '');
    videoEl.setAttribute('loop', '');
    videoEl.setAttribute('controls', '');
    videoEl.setAttribute('muted', '');
    videoEl.setAttribute('playsinline', '');

    videoEl.muted = true;
    videoEl.autoplay = true;
    videoEl.playsInline = true;
  });

  const htmlEl = doc.documentElement;
  if (!doc.head) htmlEl.insertBefore(doc.createElement('head'), htmlEl.firstChild);
  if (!doc.body) {
    const body = doc.createElement('body');
    body.innerHTML = '';
    htmlEl.appendChild(body);
  }

  const styleEl = doc.createElement('style');
  styleEl.textContent = `
    html, body {
      margin: 0;
      padding: 0;
      width: 100%;
      height: 100%;
      display: flex;
      justify-content: center;
      align-items: center;
    }
    body > * {
      max-width: 100%;
      max-height: 100%;
    }
  `;
  doc.head.appendChild(styleEl);

  doc.body.style.margin = '0';
  doc.body.style.display = 'flex';
  doc.body.style.justifyContent = 'center';
  doc.body.style.alignItems = 'center';
  doc.body.style.width = '100%';
  doc.body.style.height = '100%';

  htmlEl.style.width = '100%';
  htmlEl.style.height = '100%';
  htmlEl.style.margin = '0';
  htmlEl.style.padding = '0';

  return '<!doctype html>\n' + htmlEl.outerHTML;
}
